import React, { useEffect, useMemo, useState } from 'react';
import { COLORS } from '../utils/constants';

/**
 * Inline Sage context picker (renders inside the Sage panel, not a full-screen modal).
 */
export default function SageContextPicker({
  onCancel,
  onConfirm,
  options = [],
  selectedResumeScoreId = '',
  candidateName = '',
  email = '',
  loading = false,
}) {
  const [selected, setSelected] = useState(selectedResumeScoreId);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setSelected(selectedResumeScoreId || options[0]?.resumeScoreId || '');
    setQuery('');
  }, [selectedResumeScoreId, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const hay = [
        opt.contextTitle || opt.jobTitle,
        opt.contextSource,
        opt.stage,
        opt.status,
        opt.assessmentId,
        opt.jobPostId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const primaryButtonStyle = {
    flex: 1,
    padding: '10px 14px',
    background: COLORS.primary,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: selected ? 'pointer' : 'not-allowed',
    opacity: selected ? 1 : 0.6,
  };

  const ghostButtonStyle = {
    flex: 1,
    padding: '10px 14px',
    background: 'transparent',
    color: COLORS.textSecondary,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  };

  return (
    <div className="gg-sage-context-picker">
      <div className="gg-sage-context-picker__header">
        <h3 className="gg-sage-context-picker__title">Choose context</h3>
        <p className="gg-sage-context-picker__sub">
          {candidateName || email
            ? `Pick the application for ${candidateName || email}. Newest is selected by default.`
            : 'Pick which application Sage should use.'}
        </p>
      </div>

      <input
        type="search"
        className="gg-sage-context-picker__search"
        placeholder="Search job or assessment…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      {loading ? (
        <p className="gg-sage-context-picker__empty">Loading options…</p>
      ) : null}

      <div className="gg-sage-context-picker__list">
        {filtered.map((opt) => {
          const active = selected === opt.resumeScoreId;
          const title = opt.contextTitle || opt.jobTitle || 'Untitled';
          const sourceLabel =
            opt.contextSource === 'assessment'
              ? 'Assessment'
              : opt.contextSource === 'job'
                ? 'Job'
                : null;
          return (
            <label
              key={opt.resumeScoreId}
              className={`gg-sage-context-picker__item${active ? ' is-active' : ''}`}
            >
              <input
                type="radio"
                name="sage-context-inline"
                checked={active}
                onChange={() => setSelected(opt.resumeScoreId)}
              />
              <span>
                <div className="gg-sage-context-picker__item-title">
                  {title}
                  {sourceLabel ? (
                    <span className="gg-sage-context-picker__badge">{sourceLabel}</span>
                  ) : null}
                  {opt.overallScore != null ? (
                    <span className="gg-sage-context-picker__match">
                      {Number(opt.overallScore) <= 1
                        ? `${Math.round(Number(opt.overallScore) * 100)}% match`
                        : `${Math.round(Number(opt.overallScore))}% match`}
                    </span>
                  ) : null}
                </div>
                <div className="gg-sage-context-picker__item-meta">
                  {[
                    opt.stage || null,
                    opt.status || null,
                    opt.updatedAt
                      ? `Updated ${new Date(opt.updatedAt).toLocaleDateString()}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </span>
            </label>
          );
        })}
        {!loading && filtered.length === 0 ? (
          <p className="gg-sage-context-picker__empty">
            {options.length === 0
              ? 'No applications found for this candidate at this company.'
              : 'No matches for your search.'}
          </p>
        ) : null}
      </div>

      <div className="gg-sage-context-picker__actions">
        <button type="button" style={ghostButtonStyle} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          style={primaryButtonStyle}
          disabled={!selected}
          onClick={() => onConfirm && onConfirm(selected)}
        >
          Continue with Sage
        </button>
      </div>
    </div>
  );
}
